import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ClipboardList, Calendar, Edit, Play, CheckCircle,
  ChevronDown, Target, User, Building2, Clock, FileText, AlertTriangle, Download
} from 'lucide-react';
import api, { downloadBlob } from '../services/api';
import { getDateLocale } from '../utils/i18nHelpers';
import './PlanDetail.css';

const STATUS_CSS = {
  draft:     'plan-status-draft',
  active:    'plan-status-active',
  completed: 'plan-status-completed',
  archived:  'plan-status-archived',
};

const TYPE_CSS = {
  annual:    'plan-type-annual',
  quarterly: 'plan-type-quarterly',
  monthly:   'plan-type-monthly',
};

const TASK_STATUS_CSS = {
  not_started: 'task-status-not_started',
  in_progress: 'task-status-in_progress',
  completed:   'task-status-completed',
  overdue:     'task-status-overdue',
  cancelled:   'task-status-cancelled',
};

function PlanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['plans', 'common']);
  const [plan, setPlan] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedTasks, setExpandedTasks] = useState({});

  // Модальное окно для отчёта о выполнении
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTaskId, setReportTaskId] = useState(null);
  const [reportForm, setReportForm] = useState({
    execution_date: new Date().toISOString().split('T')[0],
    actual_result: '',
    completion_percentage: 0,
    deviation_reason: '',
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [planRes, userRes] = await Promise.all([
        api.getPlan(id),
        api.getCurrentUser(),
      ]);
      setPlan(planRes.data);
      setUser(userRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching plan:', error);
      setLoading(false);
    }
  };

  const toggleTask = (taskId) => {
    setExpandedTasks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const getProgressColor = (pct) => {
    if (pct >= 80) return 'green';
    if (pct >= 50) return 'yellow';
    return 'red';
  };

  const handleActivate = async () => {
    if (window.confirm(t('plans:actions.activateConfirm'))) {
      try {
        await api.activatePlan(id);
        fetchData();
      } catch (error) {
        alert('Ошибка: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleComplete = async () => {
    if (window.confirm(t('plans:actions.completeConfirm'))) {
      try {
        await api.completePlan(id);
        fetchData();
      } catch (error) {
        alert('Ошибка: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const openReportModal = (taskId) => {
    setReportTaskId(taskId);
    setReportForm({
      execution_date: new Date().toISOString().split('T')[0],
      actual_result: '',
      completion_percentage: 0,
      deviation_reason: '',
    });
    setShowReportModal(true);
  };

  const handleSubmitReport = async () => {
    if (!reportForm.actual_result.trim()) {
      alert(t('plans:execution.fillActualResult'));
      return;
    }
    try {
      await api.reportTaskProgress(reportTaskId, {
        ...reportForm,
        completion_percentage: parseInt(reportForm.completion_percentage),
      });
      setShowReportModal(false);
      fetchData();
    } catch (error) {
      alert('Ошибка: ' + (error.response?.data?.detail || error.message));
    }
  };

  const leadershipRoles = ['admin', 'director', 'deputy_director'];
  const isAdmin = user?.is_staff || user?.is_superuser || leadershipRoles.includes(user?.role);
  const dashboardPath = isAdmin ? '/dashboard/admin' : '/dashboard/department';

  if (loading) {
    return (
      <div className="plan-detail-page">
        <div className="loading-spinner">{t('common:table.loading')}</div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="plan-detail-page">
        <div className="empty-state">
          <h3>{t('plans:messages.notFound')}</h3>
        </div>
      </div>
    );
  }

  const statusCss = STATUS_CSS[plan.status] || STATUS_CSS.draft;
  const statusLabel = t(`plans:statusLabels.${plan.status}`, plan.status);
  const typeCss = TYPE_CSS[plan.plan_type] || '';
  const typeLabel = t(`plans:typeLabels.${plan.plan_type}`, plan.plan_type);
  const pct = plan.completion_percentage || 0;
  const progressColor = getProgressColor(pct);

  return (
    <div className="plan-detail-page">
      {/* Header */}
      <div className="plan-detail-header">
        <div className="plan-detail-header-left">
          <div>
            <h1>{plan.title}</h1>
            <p className="plan-subtitle">
              {plan.department_name} | {plan.year}
            </p>
            <div className="header-badges">
              <span className={`plan-type-badge ${typeCss}`}>
                {typeLabel}
              </span>
              <span className={`plan-status-badge ${statusCss}`}>
                {statusLabel}
              </span>
              {plan.is_overdue && (
                <span className="overdue-badge">
                  <AlertTriangle size={14} />
                  {t('plans:messages.overdue')}
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn-export" onClick={async () => {
            try { const r = await api.exportPlanFactExcel({ plan_id: id }); downloadBlob(r, `План-факт_${id}.xlsx`); } catch {}
          }}><Download size={16} /> {t('plans:actions.planFact')}</button>
          {isAdmin && plan.status === 'draft' && (
            <>
              <button className="btn-secondary" onClick={() => navigate(`/plans/${id}/edit`)}>
                <Edit size={16} />
                {t('common:buttons.edit')}
              </button>
              <button className="btn-success" onClick={handleActivate}>
                <Play size={16} />
                {t('plans:actions.activatePlan')}
              </button>
            </>
          )}
          {isAdmin && plan.status === 'active' && (
            <button className="btn-warning" onClick={handleComplete}>
              <CheckCircle size={16} />
              {t('plans:actions.completePlan')}
            </button>
          )}
        </div>
      </div>

      {/* Plan info */}
      <div className="plan-info-card">
        <div className="plan-info-grid">
          <div className="plan-info-item">
            <span className="plan-info-label">{t('plans:form.type')}</span>
            <span className="plan-info-value">{plan.plan_type_display}</span>
          </div>
          <div className="plan-info-item">
            <span className="plan-info-label">{t('common:table.department')}</span>
            <span className="plan-info-value">{plan.department_name}</span>
          </div>
          <div className="plan-info-item">
            <span className="plan-info-label">{t('plans:form.year')}</span>
            <span className="plan-info-value">{plan.year}</span>
          </div>
          <div className="plan-info-item">
            <span className="plan-info-label">{t('plans:form.period')}</span>
            <span className="plan-info-value">
              {plan.start_date && plan.end_date
                ? `${new Date(plan.start_date).toLocaleDateString(getDateLocale())} — ${new Date(plan.end_date).toLocaleDateString(getDateLocale())}`
                : '—'
              }
            </span>
          </div>
          <div className="plan-info-item">
            <span className="plan-info-label">{t('plans:actions.createdBy')}</span>
            <span className="plan-info-value">{plan.created_by_name || '—'}</span>
          </div>
          {plan.approved_by_name && (
            <div className="plan-info-item">
              <span className="plan-info-label">{t('plans:actions.approvedBy')}</span>
              <span className="plan-info-value">{plan.approved_by_name}</span>
            </div>
          )}
        </div>

        {plan.description && (
          <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '8px' }}>
            <div style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 600, marginBottom: '0.3rem' }}>
              ОПИСАНИЕ
            </div>
            <div style={{ fontSize: '0.9rem', color: '#334155', whiteSpace: 'pre-wrap' }}>
              {plan.description}
            </div>
          </div>
        )}

        {/* Overall progress */}
        <div className="plan-overall-progress">
          <span className="progress-label">{t('plans:execution.overallCompletion')}:</span>
          <div className="progress-bar">
            <div
              className={`progress-bar-fill ${progressColor}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="progress-pct">{pct}%</span>
        </div>
      </div>

      {/* Tasks */}
      <div className="tasks-section">
        <div className="tasks-section-header">
          <h2>
            <Target size={20} />
            {t('plans:tasks.titleWithCount', { count: plan.tasks?.length || 0 })}
          </h2>
        </div>

        {(!plan.tasks || plan.tasks.length === 0) ? (
          <div className="empty-state">
            <Target size={48} />
            <h3>{t('plans:tasks.noTasks')}</h3>
            <p>{t('plans:tasks.noTasksDescription')}</p>
          </div>
        ) : (
          plan.tasks.map(task => {
            const taskStatusCss = TASK_STATUS_CSS[task.status] || TASK_STATUS_CSS.not_started;
            const taskStatusLabel = t(`plans:taskStatusLabels.${task.status}`, task.status);
            const taskPct = task.completion_percentage || 0;
            const taskColor = getProgressColor(taskPct);
            const isExpanded = expandedTasks[task.id];

            return (
              <div key={task.id} className="task-card">
                <div className="task-card-header" onClick={() => toggleTask(task.id)}>
                  <div className="task-card-left">
                    <span className="task-number">{task.task_number}</span>
                    <span className="task-title">{task.title}</span>
                    <span className={`task-status-badge ${taskStatusCss}`}>
                      {taskStatusLabel}
                    </span>
                  </div>
                  <div className="task-card-right">
                    <span className={`task-deadline ${task.is_overdue ? 'overdue' : ''}`}>
                      <Calendar size={14} />
                      {new Date(task.deadline).toLocaleDateString(getDateLocale())}
                    </span>
                    <div className="task-progress-mini">
                      <div className="progress-bar">
                        <div
                          className={`progress-bar-fill ${taskColor}`}
                          style={{ width: `${taskPct}%` }}
                        />
                      </div>
                      <span className="pct">{taskPct}%</span>
                    </div>
                    <ChevronDown
                      size={18}
                      className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
                    />
                  </div>
                </div>

                {isExpanded && (
                  <div className="task-card-body">
                    <div className="task-details-grid">
                      <div className="task-detail-item">
                        <span className="task-detail-label">
                          <Building2 size={12} style={{ marginRight: 4 }} />
                          {t('plans:tasks.responsibleDepartment')}
                        </span>
                        <span className="task-detail-value">
                          {task.responsible_department_name}
                        </span>
                      </div>
                      <div className="task-detail-item">
                        <span className="task-detail-label">
                          <User size={12} style={{ marginRight: 4 }} />
                          {t('plans:tasks.responsiblePerson')}
                        </span>
                        <span className="task-detail-value">
                          {task.responsible_person_name || '—'}
                        </span>
                      </div>
                      {task.priority_name && (
                        <div className="task-detail-item">
                          <span className="task-detail-label">{t('plans:tasks.priority')}</span>
                          <span className="task-detail-value" style={{ color: task.priority_color || '#334155' }}>
                            {task.priority_name}
                          </span>
                        </div>
                      )}
                      <div className="task-detail-item">
                        <span className="task-detail-label">
                          <Clock size={12} style={{ marginRight: 4 }} />
                          {t('plans:tasks.dates')}
                        </span>
                        <span className="task-detail-value">
                          {task.start_date
                            ? `${new Date(task.start_date).toLocaleDateString(getDateLocale())} — `
                            : ''
                          }
                          {new Date(task.deadline).toLocaleDateString(getDateLocale())}
                        </span>
                      </div>
                    </div>

                    {task.expected_result && (
                      <div className="task-text-block">
                        <h4>{t('plans:tasks.expectedResult')}</h4>
                        <p>{task.expected_result}</p>
                      </div>
                    )}

                    {task.required_resources && (
                      <div className="task-text-block">
                        <h4>{t('plans:tasks.requiredResources')}</h4>
                        <p>{task.required_resources}</p>
                      </div>
                    )}

                    {task.description && (
                      <div className="task-text-block">
                        <h4>{t('plans:tasks.description')}</h4>
                        <p>{task.description}</p>
                      </div>
                    )}

                    {/* Actions */}
                    {(plan.status === 'active') && (
                      <div className="task-actions">
                        <button
                          className="btn-primary"
                          onClick={() => openReportModal(task.id)}
                        >
                          <FileText size={16} />
                          {t('plans:execution.report')}
                        </button>
                      </div>
                    )}

                    {/* Executions */}
                    {task.executions && task.executions.length > 0 && (
                      <div className="executions-section">
                        <h4>
                          <FileText size={16} />
                          {t('plans:execution.executionHistory', { count: task.executions.length })}
                        </h4>
                        {task.executions.map(exec => (
                          <div key={exec.id} className="execution-item">
                            <div className="exec-date">
                              {new Date(exec.execution_date).toLocaleDateString(getDateLocale())}
                            </div>
                            <div className="exec-content">
                              <p className="exec-result">{exec.actual_result}</p>
                              {exec.deviation_reason && (
                                <p className="exec-deviation">
                                  {t('plans:execution.deviation')}: {exec.deviation_reason}
                                </p>
                              )}
                              <span className="exec-reporter">
                                {exec.reported_by_name}
                              </span>
                            </div>
                            <div className="exec-pct">{exec.completion_percentage}%</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Report Progress Modal */}
      {showReportModal && (
        <div className="modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('plans:execution.executionReport')}</h2>
              <button className="modal-close" onClick={() => setShowReportModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('plans:execution.reportDate')}</label>
                <input
                  type="date"
                  className="form-control"
                  value={reportForm.execution_date}
                  onChange={e => setReportForm({ ...reportForm, execution_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>{t('plans:execution.actualResult')}</label>
                <textarea
                  className="form-control"
                  rows="4"
                  placeholder={t('plans:execution.actualResultPlaceholder')}
                  value={reportForm.actual_result}
                  onChange={e => setReportForm({ ...reportForm, actual_result: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>{t('plans:execution.completionPercentageLabel')}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="5"
                    className="form-control"
                    style={{ width: '100px', textAlign: 'center', fontSize: '1.1rem', fontWeight: 600 }}
                    value={reportForm.completion_percentage}
                    onChange={e => {
                      let val = parseInt(e.target.value) || 0;
                      if (val < 0) val = 0;
                      if (val > 100) val = 100;
                      setReportForm({ ...reportForm, completion_percentage: val });
                    }}
                  />
                  <span style={{ fontSize: '1.1rem', fontWeight: 600, color: '#334155' }}>%</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    style={{ flex: 1 }}
                    value={reportForm.completion_percentage}
                    onChange={e => setReportForm({ ...reportForm, completion_percentage: parseInt(e.target.value) })}
                  />
                </div>
                <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.3rem' }}>
                  {t('plans:execution.completionPercentageHint')}
                </p>
              </div>
              <div className="form-group">
                <label>{t('plans:execution.deviationReason')}</label>
                <textarea
                  className="form-control"
                  rows="2"
                  placeholder={t('plans:execution.deviationReasonPlaceholder')}
                  value={reportForm.deviation_reason}
                  onChange={e => setReportForm({ ...reportForm, deviation_reason: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowReportModal(false)}>
                {t('common:buttons.cancel')}
              </button>
              <button className="btn-primary" onClick={handleSubmitReport}>
                <FileText size={16} />
                {t('plans:execution.sendReport')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlanDetail;
